import React, { useState, useEffect } from 'react';
import { Icon } from '@lnr-v2/ui';
import { Input } from '../../atoms/Input';
import { useUpdateTranslation } from '../../../hooks/useTranslations';
import type { Translation } from '../../../services/translations.service';
import { getSectionConfig } from '../../../utils/sectionMappings';
import styles from './TranslationItem.module.css';

interface TranslationItemProps {
  translation: Translation;
}

/**
 * Format element ID to human-readable label
 */
const formatElementName = (element: string): string => {
  return element
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const TranslationItem: React.FC<TranslationItemProps> = ({
  translation,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [textEN, setTextEN] = useState(translation.textEN);
  const [textFR, setTextFR] = useState(translation.textFR);
  const [hasChanges, setHasChanges] = useState(false);

  const updateMutation = useUpdateTranslation();
  const sectionConfig = getSectionConfig(translation.section);

  // Sync with prop changes
  useEffect(() => {
    setTextEN(translation.textEN);
    setTextFR(translation.textFR);
  }, [translation.textEN, translation.textFR]);

  // Check if values have changed
  useEffect(() => {
    const changed =
      textEN !== translation.textEN || textFR !== translation.textFR;
    setHasChanges(changed);
  }, [textEN, textFR, translation.textEN, translation.textFR]);

  const handleSave = async () => {
    if (!hasChanges) {
      setIsEditing(false);
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: translation.id,
        data: {
          textEN,
          textFR,
        },
      });
      setIsEditing(false);
    } catch (error) {
      // Error is handled by the hook
      console.error('Failed to save translation:', error);
    }
  };

  const handleCancel = () => {
    setTextEN(translation.textEN);
    setTextFR(translation.textFR);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // Format page name for display
  const formatPageName = (page: string): string => {
    return page.charAt(0).toUpperCase() + page.slice(1);
  };

  // Open website URL in new tab
  const handleViewOnSite = () => {
    if (translation.websiteUrl) {
      window.open(translation.websiteUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.labelSection}>
          <div className={styles.headerTop}>
            <h4 className={styles.label}>
              {sectionConfig.label} - {formatElementName(translation.element)}
            </h4>
          </div>

          {/* Breadcrumb Navigation */}
          <div className={styles.breadcrumb}>
            <span className={styles.breadcrumbItem}>
              {formatPageName(translation.page)}
            </span>
            <Icon
              name="chevron-right"
              size="sm"
              color="var(--color-text-tertiary)"
              className={styles.breadcrumbSeparator}
            />
            <span className={styles.breadcrumbItem}>{sectionConfig.label}</span>
            <Icon
              name="chevron-right"
              size="sm"
              color="var(--color-text-tertiary)"
              className={styles.breadcrumbSeparator}
            />
            <span className={styles.breadcrumbItem}>{translation.element}</span>
          </div>
        </div>

        {/* Header Actions */}
        <div className={styles.headerActions}>
          {/* View on Site Button */}
          {translation.websiteUrl && !isEditing && (
            <button
              className={styles.viewOnSiteButton}
              onClick={handleViewOnSite}
              title="View this translation on the website"
            >
              <Icon name="external-link" size="sm" color="currentColor" />
              View on Site
            </button>
          )}

          {/* Edit Button */}
          {!isEditing && (
            <button
              className={styles.editButton}
              onClick={() => setIsEditing(true)}
              title="Edit translation"
            >
              <Icon name="edit" size="sm" color="currentColor" />
            </button>
          )}
        </div>
      </div>

      {/* Translation fields */}
      <div className={styles.fields}>
        {/* English field */}
        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            <span className={styles.flag}>🇬🇧</span>
            English
          </label>
          {isEditing ? (
            <Input
              value={textEN}
              onChange={(e) => setTextEN(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="English text"
              fullWidth
              autoFocus
            />
          ) : (
            <div className={styles.fieldValue}>{translation.textEN}</div>
          )}
        </div>

        {/* French field */}
        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            <span className={styles.flag}>🇫🇷</span>
            Français
          </label>
          {isEditing ? (
            <Input
              value={textFR}
              onChange={(e) => setTextFR(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Texte français"
              fullWidth
            />
          ) : (
            <div className={styles.fieldValue}>{translation.textFR}</div>
          )}
        </div>
      </div>

      {/* Actions */}
      {isEditing && (
        <div className={styles.actions}>
          <div className={styles.actionsLeft}>
            <span className={styles.hint}>
              Press <kbd>Cmd/Ctrl</kbd> + <kbd>Enter</kbd> to save,{' '}
              <kbd>Esc</kbd> to cancel
            </span>
          </div>
          <div className={styles.actionsRight}>
            <button
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={updateMutation.isPending}
            >
              Cancel
            </button>
            <button
              className={styles.saveButton}
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <>
                  <Icon name="spinner" size="sm" color="currentColor" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="check" size="sm" color="currentColor" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Last updated info */}
      {!isEditing && translation.User && (
        <div className={styles.footer}>
          <span className={styles.footerText}>
            Last updated by{' '}
            {translation.User.firstName || translation.User.email} on{' '}
            {new Date(translation.updatedAt).toLocaleDateString()}
          </span>
        </div>
      )}
    </div>
  );
};
